import { Component, Element, Event, h, Prop, EventEmitter, Watch } from "@stencil/core";
import { VideoplayerProgressEventArgs } from "../../constants/videoplayer-types";
import { Player } from "../player";
import pfx from "../../../../utils/style-class-prefix-helper";
import { isIphoneOrIpod } from "../../../../utils/device-helper";

@Component({
  tag: "eon-ui-youtubeplayer",
  styleUrls: ["youtube-player.scss", "../player-shared.scss"],
  shadow: false
})
export class YouTubePlayer implements Player {
  @Element() hostElement: HTMLEonUiYoutubeplayerElement;
  player: any;
  private youtubeElement: HTMLElement;
  private isIosMobileDevice: boolean;

  /**
   * Raised when the browser finished analyzing the video and can start playing, meaning it has buffered enough to begin.
   * Emits the total length of the video in seconds.
   */
  @Event() canPlayEvent: EventEmitter<number>;

  /**
   * Raised when the video has reached the end.
   */
  @Event() endedEvent: EventEmitter<void>;

  /**
   * Raised when the video resource could not be loaded due to an error (e.g. a network error).
   */
  @Event() errorEvent: EventEmitter<void>;

  /**
   * Raised when the time indicated by the currentTime attribute of the video has been updated.
   * This is triggered very frequently to keep the progress bar up2date.
   */
  @Event() timeUpdateEvent: EventEmitter<VideoplayerProgressEventArgs>;

  /**
   * The pauseEvent is fired when the video is paused via youtube button or customised control bar
   */
  @Event() pauseEvent: EventEmitter<void>;

  /**
   * The playEvent is fired when the video is played via youtube button or customised control bar
   */
  @Event() playEvent: EventEmitter<void>;

  /**
   * ID for youtube
   */
  @Prop() source: string;

  /**
   * Set to true if the player should attempt to autoplay the video.
   * This doesn't work on iPhones and requires the video to be muted as well.
   */
  @Prop() autoplay: boolean;

  /**
   * Controls whether the video is muted or not.
   */
  @Prop() isMuted: boolean;

  /**
   * Controls whether the video is currently playing or not.
   * Set to false to pause the video.
   */
  @Prop() isPlaying: boolean;

  /**
   * The seconds at which the video should play.
   */
  @Prop() seekTime: number;

  /**
   * Set to true to hide the control bar of the videoplayer.
   */
  @Prop() hideControls: boolean;

  /**
   * captions property sets Caption/Subtitles for Youtube Video
   */
  @Prop() captions: boolean = true;

  /**
   * Caption language Code can be set with languageCode property
   * As per ISO 639-1 two-letter language code: https://www.loc.gov/standards/iso639-2/php/code_list.php
   */
  @Prop() languageCode: string;

  /**
   * noCookie property will embed nocookie term into youTube video url
   * which prevent youtube from tracking user's viewing behaviour,
   * hence no activity is collected to personalise the viewing experience.
   */
  @Prop({ reflect: true }) noCookie: boolean;

  componentWillLoad() {
    this.isIosMobileDevice = isIphoneOrIpod();
    this.initVideo();
  }

  @Watch("hideControls")
  handleHideControlsChanged() {
    if (this.hideControls) {
      this.player.playVideo();
      this.player.getIframe().classList.add("hide-controls");
    } else {
      this.player.getIframe().classList.remove("hide-controls");
      this.handleIsPlayingChanged();
    }
  }

  @Watch("isPlaying")
  handleIsPlayingChanged() {
    if (this.isPlaying) {
      this.play();
    } else {
      this.pause();
    }
  }

  @Watch("isMuted")
  handleIsMutedChanged() {
    if (this.isMuted) {
      this.player.mute();
    } else {
      this.player.unMute();
    }
  }

  @Watch("autoplay")
  handleAutoplayChanged() {
    if (this.autoplay) {
      this.player.mute();
      this.player.playVideo();
      this.handleIsMutedChanged();
    }
  }

  @Watch("seekTime")
  handleSeekTimeChanged() {
    this.player.seekTo(this.seekTime, true);
  }

  private handleError(_e: YT.OnErrorEvent): void {
    this.errorEvent.emit();
  }

  private handleStateChange(event: any) {
    switch (event.data) {
      case YT.PlayerState.PLAYING:
        this.play();
        break;
      case YT.PlayerState.PAUSED:
        this.pause();
        break;
      case YT.PlayerState.ENDED:
        this.endedEvent.emit();
        break;
      case YT.PlayerState.UNSTARTED:
        break;
      case YT.PlayerState.BUFFERING:
        break;
    }
  }

  /**
   * This function creates logic inside the window object to init the youtube api
   */
  private initVideo() {
    /**
     * With the Iframe Player API, you can embed a YouTube video player on your website and control the player with JavaScript.
     */
    const iframeAPIScript = document.createElement("script");
    iframeAPIScript.src = "https://www.youtube.com/iframe_api";
    const firstScriptTag = document.getElementsByTagName("script")[0];
    firstScriptTag.parentNode.insertBefore(iframeAPIScript, firstScriptTag);

    if (!window["isReady"]) {
      window["isReady"] = false;
    }

    if (!window["callbacks"]) {
      window["callbacks"] = [];
    }

    //this is needed to support more than one youtube player on a page
    window["enqueueOnYoutubeIframeAPIReady"] = function (callback) {
      if (window["isReady"]) {
        callback();
      } else {
        window["callbacks"].push(callback);
      }
    };

    window["onYouTubeIframeAPIReady"] = function () {
      window["isReady"] = true;
      window["callbacks"].forEach(function (callback) {
        callback();
      });
      window["callbacks"].splice(0);
    };

    //enqueue the player of this component
    window["enqueueOnYoutubeIframeAPIReady"](() => {
      this.player = new window["YT"].Player(this.youtubeElement, {
        videoId: this.source,
        host: this.noCookie ? "http://www.youtube-nocookie.com" : "http://www.youtube.com",
        playerVars: {
          cc_load_policy: this.captions ? 1 : 0,
          cc_lang_pref: this.languageCode,
          autoplay: this.autoplay ? 1 : 0,
          modestbranding: 1,
          controls: 0,
          disablekb: 1,
          rel: 0,
          showinfo: 0,
          fs: 0,
          iv_load_policy: 3,
          playsinline: 0,
          // origin: "http://localhost:9001",
          loop: this.autoplay ? 1 : 0
        },
        events: {
          onReady: (e) => {
            this.handleCanPlay(e);
            if (this.isIosMobileDevice) {
              this.player.cueVideoById(this.source);
            }
          },
          onError: (e) => {
            this.handleError(e);
          },
          onStateChange: (e) => {
            this.handleStateChange(e);
            this.handleApiChange(e);
          }
        }
      });
    });
  }

  handleApiChange(_e) {
    this.player.loadModule("captions");
    this.player.getOption("captions", "track");
  }

  private handleCanPlay(_e: YT.PlayerEvent): void {
    this.canPlayEvent.emit(this.player.getDuration());
    let time_update_interval;
    // Clear any old interval.
    clearInterval(time_update_interval);

    // Start interval to update elapsed time display and
    // the elapsed part of the progress bar every second.
    time_update_interval = setInterval(() => {
      const percentPlayed = Math.round((100 / this.player.getDuration()) * this.player.getCurrentTime());
      const secondsPlayed = (percentPlayed / 100) * this.player.getDuration();

      this.timeUpdateEvent.emit({ percent: percentPlayed, seconds: secondsPlayed });
    }, 250);

    // Autoplay should work as soon as video loads on any site
    this.handleAutoplayChanged();
  }

  private play() {
    this.player.playVideo();
    this.playEvent.emit();
    this.handleAutoplayChanged();
  }

  private pause() {
    this.player.pauseVideo();
    this.pauseEvent.emit();
  }

  render() {
    return (
      <div class={pfx("youtube-wrapper")}>
        <div id={`youtube-player-${this.source}`} ref={(el) => (this.youtubeElement = el)}></div>
      </div>
    );
  }
}
